import { ButtonBase } from '@mui/material';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dropdown, IconButton, Stack, styled, Text } from 'shell/components';

const CoverImage = ({ course }) => {
    return (
        <CoverImageContainer color={course.cover_color || 'purple'}>
            <svg
                width='438'
                height='182'
                viewBox='0 0 438 182'
                fill='none'
                role='none'
                xmlns='http://www.w3.org/2000/svg'
            >
                <path
                    d='M256.78 -105.069C263.258 -169.148 308.58 -219.822 366.906 -236.417L373.512 -301.757C280.079 -286.263 204.612 -210.136 194.623 -111.324C182.676 6.861 268.758 112.215 386.994 124.114C485.504 134.027 575.048 75.6445 609.088 -12.4098L543.72 -18.9882C515.611 34.4606 457.151 68.4118 393.389 61.995C309.558 53.5585 248.424 -21.2616 256.895 -105.057L256.78 -105.069Z'
                    fill='white'
                    fillOpacity='0.1'
                />
                <path
                    d='M173.621 273.539C178.674 337.744 143.111 395.653 88.673 422.336L93.8251 487.805C183.007 455.974 243.695 367.668 235.904 268.661C226.585 150.244 123.086 61.8542 4.61167 71.133C-94.0976 78.8638 -171.803 152.208 -189.598 244.898L-124.099 239.768C-105.969 182.183 -54.4951 138.398 9.39583 133.394C93.3964 126.815 166.899 189.588 173.507 273.548L173.621 273.539Z'
                    fill='white'
                    fillOpacity='0.1'
                />
            </svg>
        </CoverImageContainer>
    );
};

const CardInformation = ({ course, handleCourseCardDropdown }) => {
    const { message } = useSnackbar();
    const { t } = useTranslation('course');

    const handleClickCopyCourseID = async (e) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(course.generated_id);
        message.success(t('course-lobby.message.course-id-copied-success', 'Course ID copied to clipboard.'));
    };

    return (
        <CardInformationContainer
            alignItems={course.role === 'instructor' ? 'flex-start' : 'center'}
            direction={'row'}
            justifyContent={'space-between'}
            spacing={4}
        >
            <Stack sx={{ minWidth: 0 }} alignItems={'flex-start'} spacing={3}>
                <Text
                    component={'p'}
                    noWrap
                    variant={'h6'}
                    weight={'semibold'}
                    tooltip={{ component: course.name || t('course-lobby.no-name-course', 'No Name'), maxWidth: 250 }}
                >
                    {course.name || t('course-lobby.no-name-course', 'No Name')}
                </Text>
                {course.role === 'instructor' && (
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text weight={'medium'}>{t('course-lobby.course-id', 'Course ID')}</Text>
                        <Button variant={'borderless'} onClick={handleClickCopyCourseID}>
                            {course.generated_id}
                        </Button>
                        <IconButton
                            aria-label={'copy course id'}
                            kind={'copy'}
                            size={'small'}
                            variant={'square'}
                            onClick={handleClickCopyCourseID}
                        />
                    </Stack>
                )}
            </Stack>
            {course.role === 'instructor' && (
                <Dropdown
                    id={`course-card-dropdown-${course.course_id}`}
                    options={[
                        {
                            key: 'archive',
                            value: course.is_archived
                                ? t('course-lobby.actions.unarchive', 'Unarchive course')
                                : t('course-lobby.actions.archive', 'Archive course'),
                        },
                    ]}
                    rootComponent={
                        <IconButton
                            aria-label={'more options'}
                            kind={'moreHorizontal'}
                            size={'medium'}
                            variant={'square'}
                        />
                    }
                    onSelect={({ key }) => handleCourseCardDropdown({ key, courseId: course.course_id })}
                />
            )}
        </CardInformationContainer>
    );
};

const CourseCard = (props) => {
    const { device } = useViewport();

    return (
        <RootButton
            className={props.className}
            component={'div'}
            device={device}
            role={'presentation'}
            onClick={() => props.onClick(props.course)}
        >
            <CoverImage {...props} />
            <CardInformation {...props} />
        </RootButton>
    );
};

const RootButton = styled(ButtonBase)(({ theme, device }) => {
    return {
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.ensightful.neutral[100]}`,
        borderRadius: '8px',
        height: '286px',
        boxShadow: theme.palette.ensightful.shadows[1],
        '&:hover': {
            borderColor: theme.palette.ensightful.primary[500],
        },
        '&:active': {
            borderColor: theme.palette.ensightful.primary[700],
        },
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
        ...(device === 'mobile' && {
            width: '100%',
        }),
        ...(device === 'tablet' && {
            width: '358px',
        }),
        ...(device === 'desktop' && {
            width: '440px',
        }),
    };
});

const CoverImageContainer = styled(Box)(({ theme, color }) => {
    return {
        borderRadius: '7px 7px 0 0',
        width: '100%',
        height: '182px',
        ...(color === 'purple' && {
            background: theme.palette.ensightful.primary[500],
        }),
        ...(color === 'green' && {
            background: theme.palette.ensightful.success[800],
        }),
        ...(color === 'red' && {
            background: theme.palette.ensightful.error[400],
        }),
        ...(color === 'dark_blue' && {
            background: theme.palette.ensightful.secondary[700],
        }),
        ...(color === 'yellow' && {
            background: theme.palette.ensightful.warning[400],
        }),
    };
});

const CardInformationContainer = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '0 0 8px 8px',
        width: '100%',
        height: '102px',
        padding: theme.spacing(5),
    };
});

export default CourseCard;
